<template>
  <div id="videoRoot" :class="playerRootClass">
    <div id="lvpsPlayerLoading" class="lvps-player-loading">
      <img src="https://lvps.6oolab.com/tail-spin.svg" />
    </div>
    <video
      id="video"
      ref="video"
      :src="source"
      :crossorigin="crossorigin"
      preload="metadata"
      controls
      playsinline
    ></video>
  </div>
</template>

<script>
import Plyr from 'plyr'
import axios from 'axios'
import Hls from 'hls.js'
import parsers from 'playlist-parser'
import $ from 'jquery'
import ga from '../lib/ga'
import platform from 'platform'

const LVPS_ID = window.LVPS_ID
const isMobile = () => {
  return ['iOS', 'Android'].includes(platform.os.family)
}
/*
const isIOS = () => {
  return platform.os.family === 'iOS'
}
*/

export default {
  data() {
    const videoId = 'video'
    const nocookie = window.location.search.includes('nocookie')

    return {
      videoId,
      $video: null,
      source: window.LVPS_MOVIE_URL,
      isLive: window.LVPS_IS_LIVE === 1,
      adEnabled: window.LVPS_AD_ENABLED === 1,
      movieAdEnabled: window.LVPS_MOVIE_AD_ENABLED === 1,
      hls: null,
      levels: [],
      hlsIsSupported: false,
      currentTime: 0,
      nocookie,
      crossorigin: nocookie ? '' : 'use-credentials'
    }
  },

  computed: {
    playerRootClass() {
      return {
        'is-live': this.$data.isLive
      }
    },
    levelHeights() {
      return this.$data.levels
        .map(level => level.height)
        .sort((a, b) => (a < b ? 1 : -1))
    },

    plyrControlsOpts() {
      const opts = this.$data.isLive
        ? ['play-large', 'play', 'mute', 'volume', 'settings']
        : [
            'play-large',
            'play',
            'progress',
            'current-time',
            'mute',
            'volume',
            'settings',
            'airplay'
          ]

      if (!this.$data.adEnabled) {
        opts.push('pip')
      }

      opts.push('fullscreen')

      return opts
    }
  },

  mounted() {
    this.initialize()
  },

  methods: {
    getLevelIndexByHeight(height) {
      return this.$data.levels.map(level => level.height).indexOf(height)
    },

    getFileByHeight(height) {
      return this.levels.filter(level => level.height === height)[0].file
    },

    async analyzeM3U() {
      const source = this.$data.source
      const M3U = parsers.M3U
      const axiosOpts = {
        withCredentials: !window.location.search.includes('nocookie')
      }

      return axios.get(source, axiosOpts).then(response => {
        const playList = M3U.parse(response.data)
        const tmp = source.split('/')
        tmp.pop()
        const fileBase = tmp.join('/')
        const results = []

        playList.forEach(item => {
          if (!item) {
            return
          }

          const { title, file } = item
          let height

          title.split(',').forEach(chunk => {
            if (chunk.includes('RESOLUTION')) {
              height = parseInt(chunk.split('x')[1])
            }
          })

          if (height) {
            const filePath = file.match(/^http/) ? file : `${fileBase}/${file}`

            results.push({ height, file: filePath })
          }
        })

        return results.sort((a, b) => a.height < b.height)
      })
    },

    async initialize() {
      this.$data.source = window.LVPS_MOVIE_URL
      this.$data.hlsIsSupported = Hls.isSupported()
      this.$data.$video = this.$refs.video
      this.attachVideoEvent()

      if (this.$data.hlsIsSupported) {
        try {
          await this.initializeHls()
          this.initializePlyr()
        } catch (e) {
          console.log('HLS load error')
        }
      } else {
        this.$data.$video.addEventListener('loadedmetadata', () => {
          this.$data.$video.currentTime = this.$data.currentTime
        })
        this.$data.levels = await this.analyzeM3U()
        this.initializePlyr()
      }
    },

    initializeHls() {
      return new Promise(resolve => {
        const nocookie = this.$data.nocookie

        this.$data.hls = new Hls({
          xhrSetup(xhr) {
            xhr.withCredentials = !nocookie
          }
        })
        this.$data.hls.loadSource(this.$data.source)
        this.$data.hls.attachMedia(this.$data.$video)

        this.$data.$video.addEventListener('loadedmetadata', () => {
          this.$data.levels = this.$data.hls.levels.map((level, index) => {
            level.index = index
            return level
          })
          resolve()
        })
      })
    },

    async initializePlyr() {
      const self = this
      const defaultQuality = isMobile() ? 360 : 720
      const options = {
        settings: ['quality'],
        controls: this.plyrControlsOpts,
        i18n: {
          quality: '画質',
          qualityLabel: {
            360: '節約モード',
            720: '標準画質',
            1080: '高画質'
          }
        },
        ads: {
          enabled: this.movieAdEnabled,
          tagUrl: window.LVPS_AD_TAG_URL
        },
        quality: {
          default: defaultQuality,
          options: this.levelHeights,
          forced: true,
          onChange: this.handleChangeLevel.bind(self)
        },
        clickToPlay: !this.$data.isLive // Liveモードでは停止させない
      }
      let bodyBGColor = document.body.style.backgroundColor

      if (this.adEnabled) {
        options.fullscreen = {
          container: '.player-container'
        }
      }

      window.plyr = new Plyr(this.$data.$video, options)

      window.plyr.on('enterfullscreen', () => {
        document.body.style.backgroundColor = 'black'
      })

      window.plyr.on('exitfullscreen', () => {
        document.body.style.backgroundColor = bodyBGColor
      })

      // 広告クリック時に動画が停止してしまうので強制的に再開する処理を追加
      if (this.movieAdEnabled) {
        window.setInterval(() => {
          if (window.plyr && window.plyr.ads && window.plyr.ads.manager) {
            window.plyr.ads.manager.resume()
          }
        }, 1000)
      }

      if (window.LVPS_IS_LIVE === 0) {
        this.addSeekButtons()
      }
    },

    handleChangeLevel(height) {
      if (this.hlsIsSupported) {
        const index = this.getLevelIndexByHeight(height)
        this.hls.currentLevel = index
      } else {
        const file = this.getFileByHeight(height)
        this.$data.currentTime = this.$data.$video.currentTime
        this.$data.source = file
      }
    },

    attachVideoEvent() {
      const $video = this.$data.$video
      let played = false

      $video.addEventListener('play', () => {
        if (!played) {
          ga.sendPlay(LVPS_ID, 1)
          played = true

          const DURATION_INTERVAL = 10 // sec
          setInterval(() => {
            if (!$video.paused) {
              ga.sendDuration(LVPS_ID, DURATION_INTERVAL)
            }
          }, DURATION_INTERVAL * 1000)
        }
      })

      $video.addEventListener('ended', () => {
        if (!this.isLive) {
          window.App.video.after()
        }
      })

      $video.addEventListener('canplay', () => {
        this.$emit('canplay')
      })
      $video.load()
    },

    addSeekButtons() {
      const SEEK_TIME = 15
      const $plyr = $('.plyr')
      const $rewind = $(
        '<div class="btn-wrap rewind"><button class="btn"></button></div>'
      )
      const $fastForward = $(
        '<div class="btn-wrap fast-forward"><button class="btn"></button></div>'
      )
      const $seekContainerSp = $('<div class="seek-container-sp"></div>')
      const $rewindSp = $(
        '<div class="area rewind"><span class="arrow"></span></div>'
      )
      const $forwardSp = $(
        '<div class="area forward"><span class="arrow"></span></div>'
      )
      let tapCount = 0
      let tapTimer = null

      // Player再読み込み時にDOMが重複して描画されるので存在する場合は削除
      if ($('.seek-container-sp').length > 0) {
        $('.seek-container-sp').remove()
      }
      if ($('.btn-wrap.rewind').length > 0) {
        $('.btn-wrap.rewind').remove()
      }
      if ($('.btn-wrap.fast-forward').length > 0) {
        $('.btn-wrap.fast-forward').remove()
      }

      if (isMobile()) {
        $seekContainerSp.append($rewindSp)
        $seekContainerSp.append($forwardSp)
        $plyr.append($seekContainerSp)
        $seekContainerSp.hide()
      } else {
        $plyr.append($rewind)
        $plyr.append($fastForward)
      }

      // 戻るボタン
      $rewind.on('click', e => {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        window.plyr.rewind(SEEK_TIME)
      })

      // 進むボタン
      $fastForward.on('click', e => {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        window.plyr.forward(SEEK_TIME)
      })

      /*
       * ダブルタップでのSeek
       * touchstartイベントだとmobile Chromeで
       * 規定時間内(おそらく1sec)に連続してタップできない問題があるためClickイベントで代用
       */
      $plyr.on('click', e => {
        if (!isMobile()) {
          return
        }
        if (tapCount === 0) {
          ++tapCount

          if (tapTimer) {
            clearTimeout(tapTimer)
          }

          tapTimer = setTimeout(() => {
            tapCount = 0
          }, 350)
        } else {
          e.preventDefault()
          clearTimeout(tapTimer)
          tapCount = 0
          $plyr.trigger('dblTap', e)
        }
      })
      $plyr.on('dblTap', (_, e) => {
        const plyrX = $plyr.offset().left
        const x = e.clientX - plyrX
        const th = $plyr.width() / 2
        const direction = x > th ? 'right' : 'left'

        $plyr
          .find('.seek-container-sp')
          .show()
          .addClass(direction)

        if (direction === 'left') {
          window.plyr.rewind(SEEK_TIME)
        } else {
          window.plyr.forward(SEEK_TIME)
        }

        setTimeout(() => {
          $plyr.find('.seek-container-sp').removeClass('right')
          $plyr.find('.seek-container-sp').removeClass('left')

          setTimeout(() => {
            $seekContainerSp.hide()
          }, 400)
        }, 300)
      })
      $plyr.find('video').on('dblclick', e => {
        // SPのフルスクリーンイベントをキャンセル
        if (isMobile()) {
          e.preventDefault()
          e.stopPropagation()
          e.stopImmediatePropagation()
        }
      })
      $seekContainerSp.on('click', e => {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
      })
    }
  }
}
</script>

<style lang="sass">
#videoRoot.is-live
  button[aria-label="Pause"]
    display: none

#videoRoot
  position: relative
  .plyr-ads
    width: 100%
    height: 100%
    z-index: 100
  #adContainer
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    z-index: 1000

.lvps-player-loading
  display: none
  position: absolute
  width: 100%
  height: 100%
  top: 0
  left: 0
  z-index: 500
  justify-content: center
  background-color: rgba(0,0,0,0.8)

.btn-wrap
  transition: opacity 400ms
  opacity: 1
  width: 80px
  height: 80px
  position: absolute
  display: flex
  justify-content: center
  align-items: center
  cursor: pointer
  position: absolute
  transform: translate(-50%,-50%)
  top: 50%

  &.rewind
    left: 25%
    .btn
      background-image: url('../assets/images/btn-rewind.svg')
      background-position: 14px center

  &.fast-forward
    left: 75%
    .btn
      background-image: url('../assets/images/btn-fast-forward.svg')
      background-position: 13px center

  &:hover
    .btn
      background-color: #00b3ff

  .btn
    background-color: rgba(0,179,255,.8)
    background-size: 20px
    background-repeat: no-repeat
    transition: all .3s ease
    width: 46px
    height: 46px
    border: none
    border-radius: 23px
    cursor: pointer

.seek-container-sp
  display: flex
  position: absolute
  top: 0
  left: 0
  width: 100%
  height: 100%

  &.right
    .area.forward
      opacity: 1
      .arrow
        opacity: 1

  &.left
    .area.rewind
      opacity: 1
      .arrow
        opacity: 1

  .area
    display: flex
    align-items: center
    justify-content: center
    background-color: rgba(255, 255, 255, 0.3)
    width: 50%
    height: 100%
    opacity: 0
    overflow: hidden
    transition: opacity 400ms
    transition-timing-function: ease-in-out

    &.rewind
      .arrow
        background-image: url('../assets/images/btn-rewind.svg')

    &.forward
      .arrow
        background-image: url('../assets/images/btn-fast-forward.svg')

    .arrow
      display: inline-block
      background-size: 30px
      background-repeat: no-repeat
      width: 30px
      height: 30px
      opacity: 0
      transition: opacity 100ms
      transition-delay: 100ms
      transition-timing-function: linear

.plyr--hide-controls
  .btn-wrap
    opacity: 0
</style>



// WEBPACK FOOTER //
// src/components/Player.vue